import { Switch, Tooltip } from '@ads/components-react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  IndividualPaymentHeaderViewModel,
  IndividualPaymentParcelDetailViewModel,
  IndividualPaymentParcelViewModel,
} from '@/shared/api/generated';
// O `Alert` do ADS é um toast (Radix Toast, renderiza `<li>` e exige provider):
// o banner inline do conteúdo é o atom do projeto.
import { Alert } from '@/shared/ui/atoms';
import { ConfirmDialog } from '@/shared/ui/molecules';

import type { ConfirmableAction } from '../../../hooks/useParcelActionRunner';
import type { BatchTargets } from '../../../utils/deriveBatchTargets';
import { formatContractDate } from '../../../utils/formatContractDate';
import { EMPTY_VALUE, formatCurrency } from '../../../utils/formatCurrency';
import { readStatus } from '../../../utils/planWriteError';
import { PARCEL_ACTION_CONFIG, type ParcelActionScope } from './actionConfig';

/** Uma linha do bloco de detalhe: rótulo e valor já formatados. */
type DetailRow = readonly [label: string, value: string];

/** Tradutor do namespace `individualPayment`. */
type Translate = TFunction<'individualPayment'>;

/**
 * Parcela alvo da ação.
 *
 * União dos dois ViewModels porque as duas telas que abrem este modal leem de
 * fontes diferentes: a listagem passa a linha da grid, e a edição passa o detalhe
 * de `GET /parcels/{parcelId}`. Os campos que o bloco de detalhe usa existem nos
 * dois — a união preserva isso sem inventar um tipo estrutural que se descolaria
 * do gerado.
 */
export type ParcelActionTarget =
  | IndividualPaymentParcelViewModel
  | IndividualPaymentParcelDetailViewModel;

/** Dados que alimentam o bloco de detalhe, conforme o escopo da ação. */
interface DetailSource {
  readonly parcel?: ParcelActionTarget | null | undefined;
  readonly header?: IndividualPaymentHeaderViewModel | null | undefined;
  readonly openParcelsCount?: number | undefined;
  readonly numberParcels?: number | undefined;
  readonly serial?: number | undefined;
}

/**
 * Rótulo da parcela: `3/12` quando o total é conhecido, `3` quando não.
 *
 * @param parcel - Parcela alvo.
 * @param numberParcels - Total de parcelas do contrato.
 * @param t - Tradutor do namespace.
 */
function parcelLabel(
  parcel: DetailSource['parcel'],
  numberParcels: number | undefined,
  t: Translate,
): string {
  if (parcel?.numberParcel === undefined) return EMPTY_VALUE;

  return numberParcels === undefined
    ? t('parcels.table.parcelNumberShort', { number: parcel.numberParcel })
    : t('parcels.table.parcelNumber', { number: parcel.numberParcel, total: numberParcels });
}

/**
 * Linhas do bloco de detalhe da ação.
 *
 * Função pura e separada por escopo: antes eram três ternários aninhados, e
 * acrescentar um escopo significava aninhar mais um.
 *
 * @param scope - O que o detalhe descreve.
 * @param source - Parcela, contrato e contagens disponíveis.
 * @param t - Tradutor do namespace.
 * @returns Linhas a exibir; vazio quando o escopo não tem detalhe próprio.
 */
function buildDetailRows(
  scope: ParcelActionScope,
  source: DetailSource,
  t: Translate,
): readonly DetailRow[] {
  const { parcel, header, openParcelsCount, numberParcels, serial } = source;

  if (scope === 'parcel') {
    return [
      [t('parcels.confirm.detail.parcel'), parcelLabel(parcel, numberParcels, t)],
      [t('parcels.confirm.detail.expiration'), formatContractDate(parcel?.expirationDate)],
      [t('parcels.confirm.detail.value'), formatCurrency(parcel?.total)],
      [t('parcels.confirm.detail.status'), parcel?.statusText ?? EMPTY_VALUE],
      // O boleto entra só quando a ação partiu da linha de um: no menu da
      // parcela não há alvo escolhido, e uma linha vazia sugeriria que houve.
      ...(serial === undefined
        ? []
        : ([[t('parcels.confirm.detail.billet'), String(serial)]] as const)),
    ];
  }

  if (scope === 'payment') {
    return [
      [t('parcels.confirm.detail.parcel'), parcelLabel(parcel, numberParcels, t)],
      [t('parcels.confirm.detail.paidAt'), formatContractDate(parcel?.paymentDate)],
      [t('parcels.confirm.detail.paidValue'), formatCurrency(parcel?.totalPaid)],
      [t('parcels.confirm.detail.status'), parcel?.statusText ?? EMPTY_VALUE],
    ];
  }

  if (scope === 'contract') {
    return [
      [t('parcels.confirm.detail.contract'), header?.courseName ?? EMPTY_VALUE],
      [t('parcels.confirm.detail.student'), header?.studentName ?? EMPTY_VALUE],
      // Sem contagem, o traço: quem abre o modal da listagem não carrega as
      // parcelas do contrato, e exibir "0" afirmaria que não há nada aberto a
      // cancelar — justamente o oposto do que esta ação faz.
      [
        t('parcels.confirm.detail.openParcels'),
        openParcelsCount === undefined ? EMPTY_VALUE : String(openParcelsCount),
      ],
      [t('parcels.confirm.detail.expectedTotal'), formatCurrency(header?.totalValue)],
    ];
  }

  return [];
}

/** Bloco cinza de pares rótulo/valor. */
function DetailList({ rows }: { readonly rows: readonly DetailRow[] }) {
  if (rows.length === 0) return null;

  return (
    <dl className="flex flex-col gap-quark rounded-sm bg-ctx-layout-body p-mini">
      {rows.map(([label, value]) => (
        <div key={label} className="flex items-center justify-between gap-micro">
          <dt className="ts-paragraph-xs text-ctx-content-placeholder">{label}</dt>
          <dd className="ts-subtitle-xxs text-right tabular-nums text-ctx-content-title">
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Amostra do conjunto atingido por uma ação em lote, com contagem e total. */
function BatchSummary({ batch }: { readonly batch: BatchTargets }) {
  const { t } = useTranslation('individualPayment');

  return (
    <div className="flex flex-col gap-micro rounded-sm bg-ctx-layout-body p-mini">
      <div className="flex items-center justify-between gap-micro">
        <span className="ts-subtitle-xxs text-ctx-content-placeholder">
          {t('parcels.confirm.batch.label')}
        </span>
        <span className="rounded-pill bg-base-gray-200 px-nano ts-caption-xxs tabular-nums text-base-gray-700">
          {batch.count}
        </span>
      </div>

      {batch.count === 0 ? (
        <span className="ts-paragraph-xs text-ctx-content-base">
          {t('parcels.confirm.batch.empty')}
        </span>
      ) : (
        <>
          <div className="flex flex-col gap-nano">
            {batch.sample.map(item => (
              <div
                key={item.id}
                className="flex items-center justify-between gap-micro tabular-nums"
              >
                <span className="ts-paragraph-xs text-ctx-content-base">
                  {t('parcels.confirm.batch.row', {
                    number: t('parcels.table.parcelNumberShort', {
                      number: item.numberParcel ?? 0,
                    }),
                    date: formatContractDate(item.expirationDate),
                  })}
                </span>
                <span className="ts-subtitle-xxs text-ctx-content-title">
                  {formatCurrency(item.total)}
                </span>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between gap-micro border-t-hairline border-ctx-layout-border pt-nano">
            <span className="ts-caption-xxs text-ctx-content-placeholder">
              {batch.hiddenCount > 0
                ? t('parcels.confirm.batch.hidden', { count: batch.hiddenCount })
                : ''}
            </span>
            <span className="ts-subtitle-xxs tabular-nums text-ctx-content-title">
              {t('parcels.confirm.batch.total', { value: formatCurrency(batch.totalValue) })}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

/** Opção booleana da ação, quando ela oferece uma. */
function ToggleOption({
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  readonly label: string;
  readonly hint: string | null;
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly onChange: (value: boolean) => void;
}) {
  /**
   * Onde o tooltip é montado — este bloco, não o `body`.
   *
   * O `ConfirmDialog` usa `<dialog>` com `showModal()`, que promove o elemento ao
   * **top layer**; o portal padrão do Radix monta no `body`, e o que está fora do
   * top layer é pintado **abaixo** dele por definição. O tooltip saía atrás do
   * backdrop, invisível, e `z-index` não resolve. Mesmo tratamento do
   * `ParcelFuturePaymentModal`.
   *
   * É estado, não `ref`: o valor precisa provocar um render quando o nó existe,
   * senão o portal já teria capturado `null` e caído no `body`.
   */
  const [tooltipContainer, setTooltipContainer] = useState<HTMLElement | null>(null);

  return (
    <div
      ref={setTooltipContainer}
      className="flex items-center gap-quark rounded-sm bg-ctx-layout-body p-mini"
    >
      <Switch
        checked={checked}
        size="sm"
        label={label}
        disabled={disabled}
        onCheckedChange={onChange}
      />
      {hint === null ? null : (
        <Tooltip title={hint} innerProps={{ portal: { container: tooltipContainer } }}>
          {/* Trigger focável de propósito: num diálogo o operador navega por
              teclado, e um tooltip só de hover esconderia a regra de quem não usa
              mouse. O `aria-label` é o próprio texto da ajuda, então focar o ícone
              já anuncia o conteúdo. */}
          <button type="button" aria-label={hint} className="inline-flex cursor-help items-center">
            <InformationCircleIcon className="size-mini shrink-0 text-ctx-content-placeholder" />
          </button>
        </Tooltip>
      )}
    </div>
  );
}

/** Erro da mutation, com o status quando o servidor respondeu um. */
function ErrorAlert({ error }: { readonly error: unknown }) {
  const { t } = useTranslation('individualPayment');
  // O corpo de erro destes endpoints é `any` no spec, então o status é o máximo
  // de informação confiável que dá para dar ao operador.
  const status = readStatus(error);

  return (
    <Alert
      type="danger"
      title={t('parcels.confirm.error.title')}
      description={
        status === null
          ? t('parcels.confirm.error.message')
          : t('parcels.confirm.error.messageWithStatus', { status })
      }
    />
  );
}

export interface ParcelActionConfirmModalProps {
  /** Ação a confirmar; `null` fecha o modal. */
  readonly action: ConfirmableAction | null;
  /** Parcela alvo — obrigatória nas ações por parcela. */
  readonly parcel?: ParcelActionTarget | null | undefined;
  /** Conjunto derivado — obrigatório nas duas ações em lote. */
  readonly batch?: BatchTargets | null | undefined;
  /** Cabeçalho do contrato — usado por "Inativar financeiro". */
  readonly header?: IndividualPaymentHeaderViewModel | null | undefined;
  /** Quantas parcelas estão em aberto; omitido exibe traço, não zero. */
  readonly openParcelsCount?: number | undefined;
  /** Total de parcelas do contrato, para compor "3/12". */
  readonly numberParcels?: number | undefined;
  /**
   * Boleto alvo, quando a ação partiu da linha de um — hoje só `reintegrate`.
   * Omitido, o detalhe não menciona boleto nenhum.
   */
  readonly serial?: number | undefined;
  /**
   * Valor da opção booleana da ação. Exigido pelas ações que declaram
   * `toggleKey` em {@link PARCEL_ACTION_CONFIG}; ignorado pelas outras.
   */
  readonly toggleValue?: boolean | undefined;
  readonly onToggleChange?: ((value: boolean) => void) | undefined;
  /** Request em voo: trava o fechamento e põe o botão em carregamento. */
  readonly isPending: boolean;
  /** Erro da mutation; exibido dentro do modal, que permanece aberto. */
  readonly error: unknown;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}

/**
 * Modal de confirmação das treze ações de escrita que só disparam um endpoint.
 *
 * Monta o conteúdo — bloco de detalhe (parcela alvo, conjunto em lote ou resumo
 * do contrato), opção booleana e alertas — sobre o {@link ConfirmDialog}; tom,
 * ícones, escopo e chaves de i18n vêm de {@link PARCEL_ACTION_CONFIG}.
 *
 * O componente **não** chama endpoint nem invalida cache — só apresenta e avisa
 * quem o abriu, que é dono da mutation.
 *
 * Enquanto `isPending`, fechar está bloqueado (X, Escape, backdrop e botão de
 * volta) e o botão de confirmação fica desabilitado, o que também evita um
 * segundo disparo por clique repetido.
 *
 * @example
 * ```tsx
 * <ParcelActionConfirmModal
 *   action={modal?.action ?? null}
 *   parcel={parcels.find(p => p.id === modal?.parcelId)}
 *   isPending={isPending}
 *   error={error}
 *   onConfirm={handleConfirm}
 *   onClose={handleClose}
 * />
 * ```
 */
export function ParcelActionConfirmModal({
  action,
  parcel,
  batch,
  header,
  openParcelsCount,
  numberParcels,
  serial,
  toggleValue = false,
  onToggleChange,
  isPending,
  error,
  onConfirm,
  onClose,
}: ParcelActionConfirmModalProps) {
  const { t } = useTranslation('individualPayment');

  if (action === null) return null;

  const config = PARCEL_ACTION_CONFIG[action];
  const count = batch?.count ?? 0;
  const hasError = error !== null && error !== undefined;

  // `closeAriaLabel` é distinto do rótulo do botão de volta de propósito: dois
  // controles com o mesmo nome acessível deixariam o fechar indistinguível para
  // leitor de tela e para teste.
  return (
    <ConfirmDialog
      open
      tone={config.tone}
      {...('icon' in config ? { icon: config.icon } : {})}
      title={t(config.titleKey, { count })}
      description={t(config.descriptionKey)}
      confirmLabel={hasError ? t('parcels.confirm.error.retry') : t(config.confirmKey, { count })}
      confirmIcon={config.confirmIcon}
      cancelLabel={t('parcels.confirm.back')}
      closeAriaLabel={t('parcels.confirm.close')}
      isPending={isPending}
      onConfirm={onConfirm}
      onClose={onClose}
    >
      <DetailList
        rows={buildDetailRows(
          config.scope,
          { parcel, header, openParcelsCount, numberParcels, serial },
          t,
        )}
      />

      {config.scope === 'batch' && batch ? <BatchSummary batch={batch} /> : null}

      {'toggleKey' in config && onToggleChange !== undefined ? (
        <ToggleOption
          label={t(config.toggleKey)}
          hint={'toggleHintKey' in config ? t(config.toggleHintKey) : null}
          checked={toggleValue}
          disabled={isPending}
          onChange={onToggleChange}
        />
      ) : null}

      {action === 'inactivateFinancial' ? (
        <Alert
          type="warning"
          title={t('parcels.confirm.inactivateFinancial.warningTitle')}
          description={t('parcels.confirm.inactivateFinancial.warningMessage')}
        />
      ) : null}

      {hasError ? <ErrorAlert error={error} /> : null}
    </ConfirmDialog>
  );
}
